/**
 * Artwork resolution.
 *
 * Demo records get procedurally generated covers. YouTube tracks use their
 * thumbnail; if the thumbnail cannot be read pixel-wise (no CORS headers) it
 * is still displayed, and a deterministic palette stands in so the record and
 * the UI accent still adapt to the track.
 */

import { loadImageLoose } from '../lib/loaders.ts';
import { fallbackPalette, paletteFrom } from '../lib/palette.ts';
import { generateCover } from '../textures/cover.ts';
import type { Artwork, Track } from '../types/media.ts';

const cache = new Map<string, Promise<Artwork>>();

const JPEG_QUALITY = 0.86;

function generated(seed: string, track: Track | null): Artwork {
  const canvas = generateCover(seed, { size: 512, palette: track?.demo?.palette ?? null });
  return {
    image: canvas,
    palette: paletteFrom(canvas) ?? fallbackPalette(seed),
    displayUrl: canvas.toDataURL('image/jpeg', JPEG_QUALITY),
    generated: true,
  };
}

async function resolve(track: Track): Promise<Artwork> {
  if (track.source === 'demo') return generated(track.id, track);

  if (track.artworkUrl) {
    const loaded = await loadImageLoose(track.artworkUrl);
    if (loaded) {
      const palette = (loaded.cors ? paletteFrom(loaded.img) : null) ?? fallbackPalette(track.id);
      return { image: loaded.img, palette, displayUrl: track.artworkUrl, generated: false };
    }
  }

  /* Nothing usable came back — draw something from the title instead. */
  return generated(`${track.artist} ${track.title}`, null);
}

/** Resolved artwork for a track. Each track is resolved at most once. */
export function resolveArtwork(track: Track | null | undefined): Promise<Artwork> {
  if (!track) {
    return Promise.resolve({
      image: null,
      palette: fallbackPalette('none'),
      displayUrl: null,
      generated: true,
    });
  }
  const hit = cache.get(track.id);
  if (hit) return hit;
  const p = resolve(track);
  cache.set(track.id, p);
  return p;
}

/** Small square thumbnail (a data URL) — used in list rows without extra requests. */
export async function thumbUrl(track: Track): Promise<string | null> {
  return (await resolveArtwork(track)).displayUrl;
}
